import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@helpers/translate";
import macbookImage from "@assets/img/macbook.png";
import "./TradingExperience.sass";

const AnimatedStat = ({ target, suffix = "", duration = 1500 }) => {
  const [value, setValue] = useState(0);
  const counterRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isAnimatingRef = useRef(false);

  useEffect(() => {
    const element = counterRef.current;

    if (!element) {
      return undefined;
    }

    const stopAnimation = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      isAnimatingRef.current = false;
    };

    const startAnimation = () => {
      stopAnimation();
      setValue(0);
      isAnimatingRef.current = true;

      const startTime = performance.now();

      const animateCounter = (currentTime) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1);

        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.round(target * easedProgress);

        setValue(currentValue);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animateCounter);
        } else {
          setValue(target);
          animationFrameRef.current = null;
          isAnimatingRef.current = false;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateCounter);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!isAnimatingRef.current) {
            startAnimation();
          }

          return;
        }

        stopAnimation();
        setValue(0);
      },
      {
        threshold: 0.4,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      stopAnimation();
    };
  }, [duration, target]);

  return (
    <span ref={counterRef}>
      {value}
      {suffix}
    </span>
  );
};

const StatItem = ({ number, suffix = "", staticValue, description }) => (
  <div className="stat-item">
    <p className="stat-item__number text-shark">
      {staticValue ?? <AnimatedStat target={number} suffix={suffix} />}
    </p>

    <p className="stat-item__description text-storm-gray">{description}</p>
  </div>
);

const TradingExperience = () => {
  const { __ } = useTranslation();

  return (
    <section id="about-us" className="trading-experience padding-container">
      <div className="trading-experience__image-container">
        <div className="trading-experience__glow" />

        <div
          className="trading-experience__image-wrapper"
          style={{
            backgroundImage: `url(${macbookImage})`,
          }}
        >
          {/* <div
            className="trading-experience__graph"
            style={{
              backgroundImage: `url(${graphImage})`,
            }}
          ></div> */}
        </div>
      </div>

      <div className="trading-experience__content">
        <h1 className="trading-experience__title text-shark">
          {__("main.tradingExperience.title")}
        </h1>

        <p className="trading-experience__subtitle text-shark">
          {__("main.tradingExperience.statistics")}
        </p>

        <div className="trading-experience__stats">
          <StatItem
            number={500}
            suffix="x"
            description={__("main.tradingExperience.maximumLeverage")}
          />

          <StatItem
            number={50}
            suffix="+"
            description={__("main.tradingExperience.tradingPairs")}
          />

          <StatItem
            staticValue="24/7"
            description={__("main.tradingExperience.customerCare")}
          />
        </div>
      </div>
    </section>
  );
};

export default TradingExperience;
